import { PageNavigation } from "../../../components/layout/PageNavigation";
import { getEmployeeTitleSuffix } from "../../onboarding/employeeIdentity";
import { useState, useRef, useEffect, useCallback } from "react";
import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import AddEmployeePage from "../../onboarding/Addemployeepage";
import CurrentEmployment from "../../onboarding/currentEmployment";
import ShiftMapping from "../../onboarding/shiftMapping";
import PreviousEmployment from "../../onboarding/PreviousEmployment";
import EducationRecords from "../../onboarding/educationRecords";
import EmployeeDocuments from "../../onboarding/EmployeeDocuments";
import AccountDetails from "../../onboarding/accountDetails";
import EmergencyInfo from "../../onboarding/EmergencyInfo";
import Assess from "../../onboarding/access/assess";
import PositionHistory from "../../onboarding/positionHistory";
import sessionDB from "../../../utils/sessionDB";
import { useNavigate, useSearchParams } from "react-router-dom";
import { User, Check, AlertCircle, BriefcaseBusiness, CalendarClock, HeartPulse, FileText, History, Building2, GraduationCap, KeyRound, Landmark, ChevronLeft, ChevronRight } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { PageHeader } from "../../../components/ui";

const ONBOARDING_SECTIONS = [
  { id: "employee-info", labelKey: "onboarding.employeeInformation", icon: User },
  { id: "current-employee", labelKey: "common.currentEmployment", icon: BriefcaseBusiness },
  { id: "shift-mappings", labelKey: "onboarding.shiftMappings", icon: CalendarClock },
  { id: "emergency-info", labelKey: "onboarding.emergencyInformation", icon: HeartPulse },
  { id: "employee-docs", labelKey: "onboarding.employeeDocuments", icon: FileText },
  { id: "position-history", labelKey: "onboarding.positionHistory", icon: History },
  { id: "previous-employment", labelKey: "pagination.previousEmployment", icon: Building2 },
  { id: "education-records", labelKey: "onboarding.educationRecords", icon: GraduationCap },
  { id: "asset-allocation", labelKey: "onboarding.assetAccessAllocation", icon: KeyRound },
  { id: "account-details", labelKey: "onboarding.accountDetails", icon: Landmark }
];

function getSectionLabel(tab) {
  return i18n.t(tab.labelKey);
}

function EmployeeSectionNavigation({ activeSection, employeeId, onSectionChange }) {
  const navigationRef = useRef(null);
  const [scrollEdges, setScrollEdges] = useState({ left: true, right: false });

  const updateScrollEdges = useCallback(() => {
    const navigation = navigationRef.current;
    if (!navigation) return;
    const left = navigation.scrollLeft <= 1;
    const right = navigation.scrollLeft + navigation.clientWidth >= navigation.scrollWidth - 1;
    setScrollEdges((current) => current.left === left && current.right === right ? current : { left, right });
  }, []);

  useEffect(() => {
    const navigation = navigationRef.current;
    if (!navigation) return;
    updateScrollEdges();
    const observer = new ResizeObserver(updateScrollEdges);
    observer.observe(navigation);
    return () => observer.disconnect();
  }, [updateScrollEdges]);

  useEffect(() => {
    const navigation = navigationRef.current;
    const activeLink = navigation?.querySelector(`[data-section="${activeSection}"]`);
    if (!activeLink) return;
    const targetLeft = navigation.scrollLeft + activeLink.getBoundingClientRect().left - navigation.getBoundingClientRect().left - (navigation.clientWidth - activeLink.offsetWidth) / 2;
    navigation.scrollTo({ left: targetLeft, behavior: "smooth" });
  }, [activeSection]);

  return (
    <nav className="flex min-w-0 items-center gap-1 border-b border-(--color-text-border)/50 pb-2" aria-label="Employee onboarding sections">
      <button type="button" aria-label="Scroll sections left" disabled={scrollEdges.left} onClick={() => navigationRef.current?.scrollBy({ left: -navigationRef.current.clientWidth * 0.7, behavior: "smooth" })} className="shrink-0 rounded-lg p-2 per-file-hint hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </button>
      <div ref={navigationRef} onScroll={updateScrollEdges} className="flex min-w-0 flex-1 gap-2 overflow-x-auto scroll-smooth scrollbar-none [&::-webkit-scrollbar]:hidden">
        {ONBOARDING_SECTIONS.map((tab) => {
          const isActive = activeSection === tab.id;
          const isDisabled = tab.id !== "employee-info" && !employeeId;
          const Icon = tab.icon;
          return (
            <a
              key={tab.id}
              href={`#${tab.id}`}
              data-section={tab.id}
              aria-current={isActive ? "step" : undefined}
              aria-disabled={isDisabled || undefined}
              tabIndex={isDisabled ? -1 : undefined}
              onClick={(event) => {
                event.preventDefault();
                if (!isDisabled) onSectionChange(tab.id);
              }}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition ${
                isActive
                  ? "bg-primary text-white shadow-[0_4px_12px_rgba(30,51,131,0.2)]"
                  : isDisabled
                  ? "opacity-50 cursor-not-allowed per-file-hint"
                  : "hover:bg-workspace per-file-hint hover:text-foreground"
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{getSectionLabel(tab)}</span>
            </a>
          );
        })}
      </div>
      <button type="button" aria-label="Scroll sections right" disabled={scrollEdges.right} onClick={() => navigationRef.current?.scrollBy({ left: navigationRef.current.clientWidth * 0.7, behavior: "smooth" })} className="shrink-0 rounded-lg p-2 per-file-hint hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </nav>
  );
}

export function EmployeeOnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEditMode = !!editId;

  const [activeSection, setActiveSection] = useState("employee-info");
  const [employeeId, setEmployeeId] = useState(editId || null);
  const [employeeIdentity, setEmployeeIdentity] = useState(null);
  const employeeTitleSuffix = getEmployeeTitleSuffix(
    employeeId,
    employeeIdentity?.empId === String(employeeId) ? employeeIdentity.name : ""
  );
  const [toast, setToast] = useState(null);
  const [rateLimitError, setRateLimitError] = useState(null);

  // Dynamic refs for each section so components can register their submit handlers independently
  const employeeInfoRef = useRef({});
  const currentEmployeeRef = useRef({});
  const shiftMappingsRef = useRef({});
  const emergencyInfoRef = useRef({});
  const employeeDocsRef = useRef({});
  const positionHistoryRef = useRef({});
  const assetAllocationRef = useRef({});
  const previousEmploymentRef = useRef({});
  const educationRecordsRef = useRef({});
  const accountDetailsRef = useRef({});

  const saveTriggers = {
    "employee-info": employeeInfoRef,
    "current-employee": currentEmployeeRef,
    "shift-mappings": shiftMappingsRef,
    "emergency-info": emergencyInfoRef,
    "employee-docs": employeeDocsRef,
    "position-history": positionHistoryRef,
    "asset-allocation": assetAllocationRef,
    "previous-employment": previousEmploymentRef,
    "education-records": educationRecordsRef,
    "account-details": accountDetailsRef
  };

  const continueAfterSaveRef = useRef(false);

  // Clear previous onboarding employee ID and cached drafts from session when starting a new onboarding flow
  useEffect(() => {
    setEmployeeIdentity(null);
    if (!isEditMode) {
      setEmployeeId(null);
      sessionDB.updateSession({
        onboardingEmployeeId: null,
        onboardingEmployeeIdentity: null,
        onboardingCurrentEmploymentDraft: null,
        onboardingPositionHistoryDraft: null
      });
    } else {
      setEmployeeId(editId);
    }
  }, [isEditMode, editId]);

  const handleSuccess = (data) => {
    const empId = data?.employeeId || data?.empId || data?.data?.employeeId || data?.data?.empId;
    if (empId) {
      setEmployeeId(String(empId));
      sessionDB.updateSession({ onboardingEmployeeId: String(empId) });
    }
    const currentIdx = ONBOARDING_SECTIONS.findIndex(s => s.id === activeSection);
    const isLastSection = currentIdx >= ONBOARDING_SECTIONS.length - 1;

    // "Continue" button: silently advance to the next tab.
    if (continueAfterSaveRef.current) {
      continueAfterSaveRef.current = false;
      if (!isLastSection) {
        setActiveSection(ONBOARDING_SECTIONS[currentIdx + 1].id);
      }
      return;
    }

    setToast(i18n.t("onboarding.savedSuccessfully"));
    if (isLastSection) {
      setTimeout(() => {
        setToast(null);
        navigate("/hrms-team/onboarding/employees");
      }, 1200);
    } else {
      setTimeout(() => setToast(null), 1200);
      setActiveSection(ONBOARDING_SECTIONS[currentIdx + 1].id);
    }
  };

  const handleSave = () => {
    continueAfterSaveRef.current = false;
    const activeRef = saveTriggers[activeSection];
    if (activeRef && activeRef.current && typeof activeRef.current.submit === "function") {
      activeRef.current.submit();
    }
  };

  const handleContinue = () => {
    if (activeSection === "employee-info" && !employeeId) {
      continueAfterSaveRef.current = true;
      if (employeeInfoRef.current && typeof employeeInfoRef.current.submit === "function") {
        employeeInfoRef.current.submit();
      }
    } else {
      const currentIdx = ONBOARDING_SECTIONS.findIndex(s => s.id === activeSection);
      if (currentIdx < ONBOARDING_SECTIONS.length - 1) {
        setActiveSection(ONBOARDING_SECTIONS[currentIdx + 1].id);
      }
    }
  };

  const handleRateLimitError = useCallback(() => {
    setRateLimitError(i18n.t("onboarding.errors.tooManyRequestsTryAgainLater"));
  }, []);

  const handleCancel = () => {
    navigate("/hrms-team/onboarding/employees");
  };

  return (
    <AppLayout formLayout="standard"
      overlay={
        toast ? (
          <div className="fixed bottom-5 right-5 z-50 animate-in fade-in slide-in-from-bottom-5 duration-300 pointer-events-none">
            <div className="flex items-center gap-2.5 bg-white border border-(--color-text-border)/60 text-foreground px-4 py-3 rounded-2xl shadow-[0_15px_40px_-10px_rgba(30,51,131,0.25)] font-sans text-sm font-medium">
              <div className="h-6 w-6 rounded-full bg-success/10 text-success grid place-items-center">
                <Check className="h-4 w-4" />
              </div>
              <span>{toast}</span>
            </div>
          </div>
        ) : null
      }
    >
      <div className="space-y-4">
        {/* Fixed Pinned Top Header & Navigation Container */}
        <div className="sticky top-[-24px] lg:top-[-28px] bg-workspace z-20 pt-2 pb-3 border-b border-(--color-text-border)/50 space-y-3">
          {/* Navigation / Header */}
          <div className="flex flex-col gap-1">
            
            <PageNavigation>
              <PageHeader back={<PageBackLink to="/hrms-team/onboarding/employees" />}
                title={(isEditMode ? i18n.t("onboarding.editEmployeeProfile") : i18n.t("onboarding.onboardNewEmployee")) + employeeTitleSuffix}
              />
            </PageNavigation>

            {rateLimitError ? (
              <div
                className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                role="alert"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{rateLimitError}</span>
              </div>
            ) : null}
          </div>

          <EmployeeSectionNavigation activeSection={activeSection} employeeId={employeeId} onSectionChange={setActiveSection} />
        </div>

        {/* Wizard Form content (Keep all sections mounted to preserve input states) */}
        <div className="bg-white rounded-2xl border border-white/60 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] space-y-6 form-surface form-section">
          {ONBOARDING_SECTIONS.map((tab) => {
            const isCurrent = activeSection === tab.id;
            const sectionLabel = getSectionLabel(tab);
            return (
              <div
                key={tab.id}
                className={isCurrent ? "space-y-4 min-h-[300px] flex flex-col justify-between" : "hidden"}
              >
                <div className="space-y-2">
                  <h3 className="text-base font-bold text-foreground">{sectionLabel}{employeeTitleSuffix}</h3>
                  {tab.id === "employee-info" ? (
                    <AddEmployeePage
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      onRateLimitError={handleRateLimitError}
                      onEmployeeIdentityChange={setEmployeeIdentity}
                      saveTriggerRef={employeeInfoRef}
                    />
                  ) : tab.id === "current-employee" ? (
                    <CurrentEmployment
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={currentEmployeeRef}
                    />
                  ) : tab.id === "shift-mappings" ? (
                    <ShiftMapping
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={shiftMappingsRef}
                    />
                  ) : tab.id === "emergency-info" ? (
                    <EmergencyInfo
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={emergencyInfoRef}
                    />
                  ) : tab.id === "employee-docs" ? (
                    <EmployeeDocuments
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={employeeDocsRef}
                    />
                  ) : tab.id === "position-history" ? (
                    <PositionHistory
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={positionHistoryRef}
                    />
                  ) : tab.id === "asset-allocation" ? (
                    <Assess
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={assetAllocationRef}
                      initialEmployeeId={employeeId}
                    />
                  ) : tab.id === "previous-employment" ? (
                    <PreviousEmployment
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={previousEmploymentRef}
                    />
                  ) : tab.id === "education-records" ? (
                    <EducationRecords
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={educationRecordsRef}
                    />
                  ) : tab.id === "account-details" ? (
                    <AccountDetails
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={accountDetailsRef}
                    />
                  ) : (
                    <div className="p-8 border border-dashed border-(--color-text-border) rounded-2xl bg-workspace/10 text-center flex flex-col items-center justify-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-primary/10 text-primary grid place-items-center">
                        <User className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {sectionLabel} {i18n.t("onboarding.formComponentSuffix")}
                        </p>
                        <p className="text-xs per-file-hint mt-1">
                          <Trans i18nKey="serviceExplorer.formInputsValidationLogicAndBackendApiHandlers" />
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Single Shared Navigation Footer */}
          <div className="flex justify-between items-center pt-6 border-t border-(--color-text-border)/35 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="h-10 px-5 rounded-xl border text-xs sm:text-sm font-semibold bg-white per-file-hint hover:text-foreground hover:bg-workspace"
            >
              <Trans i18nKey="formActions.cancel" />
            </Button>
            
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleSave}
                className="h-10 px-5 rounded-xl border border-primary/35 text-primary bg-white hover:bg-slate-50 text-xs sm:text-sm font-semibold"
              >
                <Trans i18nKey="formActions.save" />
              </Button>
              
              {activeSection !== "account-details" && (
                <Button
                  type="button"
                  onClick={handleContinue}
                  className="h-10 px-5 rounded-xl bg-primary text-white hover:bg-accent text-xs sm:text-sm font-semibold"
                >
                  <Trans i18nKey="formActions.continue" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
